import React from 'react'
import cx from 'classnames'
import cn from 'classnames'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { BookEnum } from '@interfaces/BookEnum'
import { WritingListStatusEnum } from '@interfaces/WritingListStatusEnum'
import { Pagination } from '@components/Pagination'
import { Button } from '@components/Buttons'
import { Checkbox } from '@components/Checkbox'
import SelectPerPage from '@components/Pagination/components/SelectPerPage/SelectPerPage'
import { Loading } from '@components/Loading'
import { ToggleSwitch } from '@components/ToggleSwitch'
import { useWindowSize } from '@hooks/useWindowSize'
import { CommentIcon } from '@components/icons/CommentIcon'
import { EditeIcon } from '@components/icons/EditeIcon'
import { HeartSolidIcon } from '@components/icons/HeartSolidIcon'
import { ListIcon } from '@components/icons/ListIcon'
import { PlusIcon } from '@components/icons/PlusIcon'
import { ViewIcon } from '@components/icons/ViewIcon'
import { formatDateDomini } from '@lib/utils'
import { ClockIcon } from '@components/icons/ClockIcon'
import { MutiSelectTableProps } from './interface'
import ButtonSelectStatus from '../ButtonSelectStatus/ButtonSelectStatus'
import { Box } from '@features/story/components/Box'
import { promotionDate } from '@lib/promotion/utils'

export function MutiSelectTable({
  data,
  loading,
  page,
  perpage,
  total,
  selectedIds,
  writingDisabled,
  pageChange,
  perpageChange,
  handleSelect,
  handleSelectAll,
  changeStatusGroup,
  changeStatus,
  openCreateStoryModal,
  navigateToStoryEditPage,
  storyMenuOptions,
}: MutiSelectTableProps) {
  const router = useRouter()
  const { isMobile } = useWindowSize()

  const perPageOptions = [
    {
      label: '10',
      value: 10,
    },
    {
      label: '20',
      value: 20,
    },
    {
      label: '30',
      value: 30,
    },
  ]

  return (
    <div>
      <div className='flex justify-between mb-[20px] mobile:flex-col'>
        <div className='flex text-secondary-1 font-light space-x-[5px] text-[14px] mobile:mb-[10px]'>
          <p>จำนวนงานเขียน :</p>
          <p className='font-bold text-secondary dark:text-dark-10'>
            {total.toLocaleString()}
          </p>
          <p>รายการ</p>
        </div>
        <SelectPerPage
          value={perpage}
          options={perPageOptions}
          handleSelect={perpageChange}
          className='mobile:w-full'
        />
      </div>
      {loading ? (
        <div className='flex justify-center pt-[50px]'>
          <Loading size={50} />
        </div>
      ) : (
        <>
          {data.length > 0 && !loading ? (
            <div className='flex flex-col w-full'>
              <div className='flex mobile:flex-col desktop:items-center justify-between w-full py-[20px] mobile:py-[10px] border border-gray-4 dark:border-purple rounded-[8px] shadow-card mb-[20px]'>
                <div className='flex items-center px-[20px] mobile:px-[10px] mobile:mb-[20px]'>
                  <Checkbox
                    id='select'
                    checked={selectedIds.length === data.length}
                    onChange={handleSelectAll}
                    className='w-[40px]'
                  />
                  <label
                    htmlFor='select'
                    className='text-[14px] text-secondary dark:text-dark-10 font-light ml-[10px] text-left w-[140px]'
                  >
                    เลือกทั้งหมด
                  </label>
                  <Button
                    type='button'
                    className='bg-primary dark:bg-purple text-white dark:text-white-2 h-[40px] desktop-tablet:hidden mobile:w-[160px]'
                    icon={<PlusIcon />}
                    disabled={writingDisabled}
                    onClick={openCreateStoryModal}
                  >
                    <span className='text-[14px] whitespace-nowrap font-medium'>
                      เพิ่มงานเขียน
                    </span>
                  </Button>
                </div>
                <div className='flex font-mitr justify-center items-center px-[20px] mobile:px-[10px] mobile:whitespace-nowrap'>
                  <Button
                    type='button'
                    className='bg-blue-2 dark:bg-purple px-[20px] h-[30px] mobile:w-[90px] mr-[10px]'
                    onClick={() =>
                      changeStatusGroup(
                        selectedIds,
                        WritingListStatusEnum.PUBLISHED
                      )
                    }
                    disabled={selectedIds.length === 0}
                  >
                    <span className='text-[14px] text-primary dark:text-white-2 font-medium'>
                      เผยแพร่
                    </span>
                  </Button>
                  <Button
                    type='button'
                    className='bg-gray-4 dark:bg-transparent dark:border dark:border-dark-10 px-[20px] h-[30px] mobile:w-[105px] mr-[10px]'
                    onClick={() =>
                      changeStatusGroup(
                        selectedIds,
                        WritingListStatusEnum.DRAFT
                      )
                    }
                    disabled={selectedIds.length === 0}
                  >
                    <span className='text-[14px] text-secondary dark:text-dark-10 font-medium'>
                      ไม่เผยแพร่
                    </span>
                  </Button>
                  <Button
                    type='button'
                    className='bg-red-1 px-[20px] mobile:w-[90px] h-[30px] desktop-tablet:mr-[10px]'
                    onClick={() =>
                      changeStatusGroup(
                        selectedIds,
                        WritingListStatusEnum.DELETE
                      )
                    }
                    disabled={selectedIds.length === 0}
                  >
                    <span className='text-[14px] text-red font-medium'>ลบ</span>
                  </Button>
                  <Button
                    data-test='createStory'
                    type='button'
                    className='bg-primary dark:bg-purple text-white dark:text-white-2 h-[40px] desktop-tablet:ml-[10px] mobile:hidden'
                    icon={<PlusIcon />}
                    disabled={writingDisabled}
                    onClick={openCreateStoryModal}
                  >
                    <span className='text-[14px] whitespace-nowrap font-medium'>
                      เพิ่มงานเขียน
                    </span>
                  </Button>
                </div>
              </div>

              <div className='grid grid-cols-1 gap-y-[20px] mb-[30px]'>
                {data?.map((book, index) => (
                  <div
                    key={book.id}
                    className={cx(
                      'border border-gray-4 dark:border-purple shadow-card text-secondary dark:text-dark-10 text-[14px] flex py-[10px] rounded-[8px] mobile:flex-col',
                      {
                        'bg-gray-4 dark:bg-dark-8 border !border-gray-3 dark:!border-primary':
                          selectedIds.includes(book.id),
                      }
                    )}
                  >
                    <div className='flex items-center w-full mobile:items-start mobile:mb-[20px]'>
                      <div className='flex px-[20px] mobile:px-[10px] mobile:my-auto'>
                        <Checkbox
                          checked={selectedIds.includes(book.id)}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            event.stopPropagation()
                            handleSelect(book.id)
                          }}
                        />
                      </div>
                      <div className='relative w-[92px]  mobile:w-[57px] h-[129.38px] mobile:h-[80.16px]  bg-gray-3 rounded-[8px] overflow-hidden shrink-0'>
                        {book.coverResizeImgPath || book.coverImgPath ? (
                          <Image
                            src={book.coverResizeImgPath || book.coverImgPath}
                            alt='coverImgPath'
                            layout='fill'
                          />
                        ) : null}
                      </div>
                      <div className='ml-[20px] py-[10px] mobile:ml-[18px] mobile:h-full mobile:py-0'>
                        <div
                          data-test={`book-type-${book.id}`}
                          className='text-[14px] font-light mb-[18px] mobile:mb-[10px]'
                        >
                          {book.bookType === BookEnum.NOVEL && 'นิยาย'}
                          {book.bookType === BookEnum.MANGA && 'การ์ตูน'}
                        </div>

                        <div
                          data-test={`title-${book.id}`}
                          className='mt-[10px] font-medium text-base font-mitr w-full line-clamp-1 mobile:line-clamp-2 break-word break-all mb-[23px] mobile:mb-0 tablet:mb-[10px]'
                        >
                          {!isMobile && 'เรื่อง'} {book.title}
                        </div>
                        {!!book?.publishedUntil && (
                          <div className='desktop-tablet:hidden w-full mt-1 flex-wrap'>
                            <div className=' flex flex-wrap min-h-6 mr-[10px] py-0.5 items-center px-2 text-xs rounded bg-red-3 text-white'>
                              <ClockIcon className='w-4 h-4 mr-[6px]' />
                              <span>ปิดเผยแพร่</span>
                              <span className='pl-1'>
                                {formatDateDomini(
                                  book?.publishedUntil,
                                  true,
                                  true
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                        <div
                          className={cx(
                            'flex items-center  font-light tablet:grid tablet:grid-cols-2 tablet:gap-y-[10px] mobile:hidden'
                          )}
                        >
                          <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px] mr-[10px]'>
                            <ViewIcon
                              width='24'
                              height='24'
                              className='text-primary'
                            />
                            <span className='text-secondary-1 text-base'>
                              {book.viewCount.toLocaleString()}
                            </span>
                          </div>
                          <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px] mr-[10px]'>
                            <CommentIcon
                              width='24'
                              height='24'
                              className='text-primary'
                            />
                            <span className='text-secondary-1 text-base'>
                              {book.commentCount.toLocaleString()}
                            </span>
                          </div>
                          <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px] mr-[10px]'>
                            <HeartSolidIcon
                              width='24'
                              height='24'
                              className='text-red'
                            />
                            <span className='text-secondary-1 text-base'>
                              {book.likeCount.toLocaleString()}
                            </span>
                          </div>
                          <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px]'>
                            <div className='w-[17px] h-[18px] min-w-[17px] min-h-[18px]'>
                              <Image
                                src='/coin.webp'
                                width='100%'
                                height='100%'
                              />
                            </div>
                            <span className='text-secondary-1 text-base'>
                              {book.amountSpent.toLocaleString()}
                            </span>
                          </div>
                          <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px]'>
                            {book.bookChapterCampaignHeader && (
                              <Box.Text
                                className={cn(
                                  'h-fit whitespace-nowrap text-sm py-1 rounded px-2 text-white dark:text-white-2 min-w-[200px] text-center w-auto',
                                  {
                                    'bg-red-3':
                                      book.bookChapterCampaignHeader
                                        ?.minSellingPrice > 0,
                                    'bg-blue-1':
                                      book.bookChapterCampaignHeader
                                        ?.minSellingPrice <= 0,
                                  }
                                )}
                              >
                                {book.bookChapterCampaignHeader
                                  ?.minSellingPrice > 0
                                  ? 'ลดราคา'
                                  : 'อ่านฟรี'}{' '}
                                {promotionDate(
                                  book.bookChapterCampaignHeader?.startedAt,
                                  book.bookChapterCampaignHeader?.endedAt,
                                  '',
                                  false,
                                  false
                                )}
                              </Box.Text>
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        data-test='column-information'
                        className={cx(
                          'flex flex-col h-full justify-between desktop-tablet:pr-[20px] ml-auto py-[10px] mobile:hidden'
                        )}
                      >
                        <div className='flex flex-wrap w-full gap-[17px] justify-end'>
                          {!!book?.publishedUntil && (
                            <div className='flex h-6 flex-wrap items-center px-2 py-0 text-xs rounded bg-red-3 text-white'>
                              <ClockIcon className='w-4 h-4 mr-[6px]' />
                              <span>ปิดเผยแพร่</span>
                              <span className='pl-1'>
                                {formatDateDomini(
                                  book?.publishedUntil,
                                  true,
                                  true
                                )}
                              </span>
                            </div>
                          )}
                          <div className='flex items-center space-x-[17px]'>
                            <p
                              className={cx('font-light text-[14px]', {
                                ' text-secondary dark:text-dark-10':
                                  book.status ===
                                  WritingListStatusEnum.PUBLISHED,
                                'text-secondary-1':
                                  book.status === WritingListStatusEnum.DRAFT,
                              })}
                            >
                              เผยแพร่
                            </p>
                            <ToggleSwitch
                              checked={
                                book.status === WritingListStatusEnum.PUBLISHED
                              }
                              disabled={!book.isVisible}
                              onChange={() => {
                                changeStatus(
                                  book.id,
                                  book.status ===
                                    WritingListStatusEnum.PUBLISHED
                                    ? WritingListStatusEnum.DRAFT
                                    : WritingListStatusEnum.PUBLISHED
                                )
                              }}
                            />
                          </div>
                        </div>

                        <div className='flex space-x-[10px] justify-end'>
                          <button
                            data-test={`preview-${book.id}`}
                            onClick={() =>
                              router.push(`/my-writing/${book.id}`)
                            }
                            className=' border border-gray-3 dark:border-dark-9 bg-white dark:bg-dark-9 rounded-[8px] px-[4px] h-[30px]'
                          >
                            <ListIcon
                              width='20'
                              height='20'
                              className='text-secondary-1 dark:text-dark-10'
                            />
                          </button>
                          <button
                            data-test={`my-writing-book-row-${index}`}
                            className=' border border-gray-3 dark:border-dark-9 bg-white dark:bg-dark-9 rounded-[8px] px-[4px] h-[30px]'
                            onClick={() => navigateToStoryEditPage(book.id)}
                          >
                            <EditeIcon
                              width='20'
                              height='20'
                              className='text-secondary-1 dark:text-dark-10'
                            />
                          </button>
                          <ButtonSelectStatus
                            title={book.title}
                            id={book.id}
                            options={storyMenuOptions(book)}
                            handleSelect={changeStatus}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='desktop-tablet:hidden pl-[44px] pr-[20px]'>
                      <div
                        className={cx(
                          'grid grid-cols-2 items-center gap-[10px] font-light'
                        )}
                      >
                        <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px]'>
                          <ViewIcon
                            width='18'
                            height='18'
                            className='text-primary'
                          />
                          <span className='text-secondary-1 text-sm'>
                            {book.viewCount.toLocaleString()}
                          </span>
                        </div>
                        <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px]'>
                          <CommentIcon
                            width='18'
                            height='18'
                            className='text-primary'
                          />
                          <span className='text-secondary-1 text-sm'>
                            {book.commentCount.toLocaleString()}
                          </span>
                        </div>
                        <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px]'>
                          <HeartSolidIcon
                            width='18'
                            height='18'
                            className='text-red'
                          />
                          <span className='text-secondary-1 text-sm'>
                            {book.likeCount.toLocaleString()}
                          </span>
                        </div>
                        <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[60px]'>
                          <div className='w-[17px] h-[18px]'>
                            <Image
                              src='/coin.webp'
                              width='100%'
                              height='100%'
                            />
                          </div>
                          <span className='text-secondary-1 text-sm'>
                            {book.amountSpent.toLocaleString()}
                          </span>
                        </div>
                        <div className='flex items-center space-x-[5px] w-[129px] mobile:w-[200px]'>
                          {book.bookChapterCampaignHeader && (
                            <Box.Text
                              className={cn(
                                'h-fit whitespace-nowrap text-sm py-1 rounded px-2 text-white dark:text-white-2 min-w-[200px] text-center',
                                {
                                  'bg-red-3':
                                    book.bookChapterCampaignHeader
                                      ?.minSellingPrice > 0,
                                  'bg-blue-1':
                                    book.bookChapterCampaignHeader
                                      ?.minSellingPrice <= 0,
                                }
                              )}
                            >
                              {book.bookChapterCampaignHeader?.minSellingPrice >
                              0
                                ? 'ลดราคา'
                                : 'อ่านฟรี'}{' '}
                              {promotionDate(
                                book.bookChapterCampaignHeader?.startedAt,
                                book.bookChapterCampaignHeader?.endedAt,
                                '',
                                false,
                                false
                              )}
                            </Box.Text>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      data-test='column-information'
                      className={cx(
                        'flex h-full justify-between pl-[44px] pr-[10px] pt-[20px] desktop-tablet:hidden'
                      )}
                    >
                      <div className='flex items-center gap-x-[17px] gap-y-[10px]'>
                        <p
                          className={cx('font-light text-[14px]', {
                            ' text-secondary dark:text-dark-10':
                              book.status === WritingListStatusEnum.PUBLISHED,
                            'text-secondary-1':
                              book.status === WritingListStatusEnum.DRAFT,
                          })}
                        >
                          เผยแพร่
                        </p>
                        <ToggleSwitch
                          checked={
                            book.status === WritingListStatusEnum.PUBLISHED
                          }
                          disabled={!book.isVisible}
                          onChange={() => {
                            changeStatus(
                              book.id,
                              book.status === WritingListStatusEnum.PUBLISHED
                                ? WritingListStatusEnum.DRAFT
                                : WritingListStatusEnum.PUBLISHED
                            )
                          }}
                        />
                      </div>
                      <div className='flex space-x-[10px] justify-end'>
                        <button
                          data-test={`preview-${book.id}`}
                          onClick={() => router.push(`/my-writing/${book.id}`)}
                          className=' border border-gray-3 dark:border-dark-9 bg-white dark:bg-dark-9 rounded-[8px] px-[4px] h-[30px]'
                        >
                          <ListIcon
                            width='20'
                            height='20'
                            className='text-secondary-1 dark:text-dark-10'
                          />
                        </button>
                        <button
                          data-test={`my-writing-book-row-${index}`}
                          className=' border border-gray-3 dark:border-dark-9 bg-white dark:bg-dark-9 rounded-[8px] px-[4px] h-[30px]'
                          onClick={() => navigateToStoryEditPage(book.id)}
                        >
                          <EditeIcon
                            width='20'
                            height='20'
                            className='text-secondary-1 dark:text-dark-10'
                          />
                        </button>
                        <ButtonSelectStatus
                          title={book.title}
                          id={book.id}
                          options={storyMenuOptions(book)}
                          handleSelect={changeStatus}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Pagination
                page={page}
                perpage={perpage}
                total={total}
                pageChange={pageChange}
              />
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center pt-[80px]'>
              <h1 className='text-[24px] font-medium text-gray-12 dark:text-dark-10 font-mitr mb-[20px]'>
                ไม่พบงานเขียน
              </h1>
              <Button
                data-test='createStory'
                type='button'
                className='bg-primary dark:bg-purple text-white dark:text-white-2 h-[40px]'
                icon={<PlusIcon />}
                disabled={writingDisabled}
                onClick={openCreateStoryModal}
              >
                <span className='text-[14px] whitespace-nowrap'>
                  เพิ่มงานเขียน
                </span>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
